import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import NotificationBell from '../NotificationBell';
import { 
  LayoutDashboard, 
  Dumbbell, 
  Utensils, 
  Target, 
  MessageCircle, 
  Users, 
  CreditCard,
  ShoppingBag,
  Settings,
  Menu,
  X,
  LogOut,
  ChevronRight,
  BarChart3,
  TrendingUp,
  Trophy,
  Apple,
  Calendar,
  ClipboardCheck,
  Gift,
  Activity,
  Megaphone,
  UsersRound,
  Watch,
  Palette,
  PieChart,
  CalendarClock,
  Library,
  BookOpen,
  MessageSquare,
  Calculator,
  Music,
  ScanLine,
  UserCog
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dfed4268-137d-4b89-bcab-122f53e08876/artifacts/k5gcf49q_BBA%20LOGO.webp";

// Custom Logo Component with clean styling
const Logo = ({ className = "h-12" }) => (
  <div className={`relative ${className}`} style={{ aspectRatio: '1/1' }}>
    <img 
      src={LOGO_URL} 
      alt="Bodies By Akeem" 
      className="h-full w-auto rounded-full"
      style={{ 
        background: 'transparent',
        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.5))'
      }}
    />
  </div>
);

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/announcements', label: 'Announcements', icon: Megaphone },
  { path: '/teams', label: 'Teams', icon: UsersRound },
  { path: '/workouts', label: 'Workouts', icon: Dumbbell },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/tools', label: 'Tools', icon: Calculator },
  { path: '/workout-music', label: 'Workout Music', icon: Music },
  { path: '/checkins', label: 'Check-ins', icon: ClipboardCheck },
  { path: '/nutrition', label: 'Nutrition', icon: Utensils },
  { path: '/food-log', label: 'Food Log', icon: Apple },
  { path: '/food-scanner', label: 'Food Scanner', icon: ScanLine },
  { path: '/wearables', label: 'Wearables', icon: Watch },
  { path: '/habits', label: 'Habits', icon: Target },
  { path: '/my-progress', label: 'My Progress', icon: TrendingUp },
  { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  { path: '/referrals', label: 'Referrals', icon: Gift },
  { path: '/messages', label: 'Messages', icon: MessageCircle },
  { path: '/community', label: 'Community', icon: Users },
  { path: '/forum', label: 'Forum', icon: MessageSquare },
  { path: '/subscription', label: 'Subscription', icon: CreditCard },
  { path: '/marketplace', label: 'Marketplace', icon: ShoppingBag },
  { path: '/clients', label: 'Client Management', icon: UserCog, trainerOnly: true },
  { path: '/trainer', label: 'Trainer Hub', icon: BarChart3, trainerOnly: true },
  { path: '/analytics', label: 'Analytics', icon: PieChart, trainerOnly: true },
  { path: '/campaigns', label: 'Campaigns', icon: CalendarClock, trainerOnly: true },
  { path: '/programs', label: 'Programs', icon: Library, trainerOnly: true },
  { path: '/exercises', label: 'Exercises', icon: BookOpen, trainerOnly: true },
  { path: '/forms', label: 'Forms', icon: ClipboardCheck, trainerOnly: true },
  { path: '/branding', label: 'Branding', icon: Palette, trainerOnly: true },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function MainLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Mobile Header */}
      <header className="lg:hidden glass-header fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 h-16">
          <button 
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <Link to="/dashboard">
            <Logo className="h-10" />
          </Link>
          
          <NotificationBell />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50
        transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              <Logo className="h-12" />
            </Link>
            <button 
              className="lg:hidden p-2 text-gray-400 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-2">
              {navItems
                .filter(item => !item.trainerOnly || user?.role === 'trainer' || user?.role === 'admin')
                .map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-sm transition-all
                        ${isActive 
                          ? 'bg-primary text-white' 
                          : 'text-gray-400 hover:bg-secondary hover:text-white'
                        }
                        ${item.trainerOnly ? 'border-l-2 border-primary/50' : ''}
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                      {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <Avatar>
                <AvatarFallback className="bg-primary/20 text-primary">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </div>
                <div className="text-xs text-gray-400 truncate">{user?.email}</div>
              </div>
            </div>
            
            <Button
              data-testid="sidebar-logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-gray-400 hover:text-white hover:bg-secondary"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Desktop Header */}
        <header className="hidden lg:flex glass-header h-16 items-center justify-between px-6 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <h2 className="font-heading text-xl text-white uppercase">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h2>
          </div>
          
          <div className="flex items-center gap-4">
            <NotificationBell />
            
            <div className="flex items-center gap-3">
              <Avatar className="w-8 h-8">
                <AvatarFallback className="bg-primary/20 text-primary text-sm">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="text-sm">
                <div className="text-white font-medium">{user?.first_name}</div>
                <div className="text-xs text-gray-400 capitalize">{user?.role}</div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
